import { DeferredPromise, pDefer } from '@naturalcycles/js-lib'
import { PublicKeyTransport, SigningKeyVault, signingKeyVault } from '@naturalcycles/shared'
import { getSignalStore, getState } from '../store/signalStore.js'
import { logger } from '../util/log.util.js'
import { sentry } from '../util/sentry.util.js'

class SessionSigningService {
  private keyVault: SigningKeyVault | null = null
  private initCache: DeferredPromise | null = null

  public async init(): Promise<void> {
    if (this.initCache) return await this.initCache
    this.initCache = pDefer()

    const { sessionEncryptionKeys } = getState()

    const hasStoreRemovedKeys = !sessionEncryptionKeys && this.keyVault
    if (hasStoreRemovedKeys) {
      this.removeKeyPair()
    }

    const hasStoreAddedKeys = sessionEncryptionKeys && !this.keyVault
    if (hasStoreAddedKeys) {
      this.keyVault = await signingKeyVault(sessionEncryptionKeys)
    }

    if (this.keyVault) {
      this.initCache.resolve()
      this.initCache = null
      return
    }

    this.keyVault = await signingKeyVault.generateKeyPair()

    const newKeyPair = await this.keyVault.exportKeyPair()
    getSignalStore().$sessionEncryptionKeys.set(newKeyPair)
    this.initCache.resolve()
    this.initCache = null
  }

  public async sign(sessionId: string, ts: number): Promise<string> {
    await this.init()
    const newSessionId = getSignalStore().$sessionId()
    if (newSessionId !== sessionId) {
      logger.log('ids', { newSessionId, sessionId })
      sentry.captureException(new Error('Session ID mismatch'))
    }
    const signature = await this.keyVault!.sign(`${sessionId}|${ts}`)

    return signature
  }

  public async getPublicKey(): Promise<PublicKeyTransport> {
    await this.init()
    const publicKey = await this.keyVault!.exportPublicKey()
    return publicKey
  }

  public removeKeyPair(): void {
    this.keyVault = null
  }
}

export const sessionSigningService = new SessionSigningService()
